/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { PaymentsCatalog } from '@/components/content/CheckOut/parts/Payment/utils/getPaymentsCatalog';

export type CartItem = {
	name: string;
	quant: number;
};

export type Cart = {
	items: CartItem[];
};

export type OrderAdjustment = {
	code?: string;
	description?: string;
	displayLevel?: string;
	currency?: string;
	amount?: string;
	usage?: string;
	userDataField?: { value?: string; key: string }[];
	descriptionLanguage?: string;
	xadju_calUsageId?: string | null;
};

type StoreIdentifier = {
	uniqueID?: string;
	externalIdentifier?: { ownerID?: string; nameIdentifier?: string };
};

type AssociatedPromotion = {
	description?: { value?: string; language?: string };
	promotionIdentifier?: {
		calculationCodeIdentifier?: {
			calculationCodeExternalIdentifier?: {
				calculationUsageID?: string;
				code?: string;
				storeIdentifier?: StoreIdentifier;
			};
			uniqueID?: string;
		};
		uniqueID?: string;
		externalIdentifier?: {
			storeIdentifier?: StoreIdentifier;
			version?: number;
			name: string;
			revision?: number;
		};
	};
};

export type Order = {
	adjustment?: OrderAdjustment[];
	buyerDistinguishedName: string;
	buyerId: string;
	buyerPONumber?: string;
	channel: Channel;
	contracts?: {
		list: Record<string, string>[];
		byId: Record<string, any>;
	};
	orderEditor?: {
		distinguishedName: string;
		externalIdentifier: { identifier: string };
		uniqueID: string;
	};
	grandTotal: string;
	grandTotalCurrency: string;
	lastUpdateDate: string;
	locked: string;
	orderId: string;
	orderItem: OrderItem[];
	orderStatus: string;
	orderDescription: string;
	orderTypeCode: string;
	orgDistinguishedName: string;
	orgUniqueID: string;
	paymentInstruction: PaymentInstruction[];
	placedDate: string;
	prepareIndicator: string;
	promotionCode?: {
		userData?: { userDataField?: { value?: string; key: string }[] };
		associatedPromotion?: AssociatedPromotion[];
		code?: string;
		reason?: {
			reasonCode?: string;
			valid?: boolean;
			description?: { value?: string; language?: string };
			userData?: { userDataField?: { value?: string; key: string }[] };
		};
	}[];
	recordSetComplete: string;
	recordSetCount: string;
	recordSetStartNumber: string;
	recordSetTotal: string;
	resourceId: string;
	resourceName: string;
	shipAsComplete: string;
	storeNameIdentifier: string;
	storeUniqueID: string;
	totalAdjustment: string;
	totalAdjustmentCurrency: string;
	totalProductPrice: string;
	totalProductPriceCurrency: string;
	totalSalesTax: string;
	totalSalesTaxCurrency: string;
	totalShippingCharge: string;
	totalShippingChargeCurrency: string;
	totalShippingTax: string;
	totalShippingTaxCurrency: string;
	x_firstName: string;
	x_isPersonalAddressesAllowedForShipping: string;
	x_isPurchaseOrderNumberRequired: string;
	x_lastName: string;
	x_trackingIds: string;
};

export type UserDataField = {
	value: string;
	key: string;
};

export type Channel = {
	channelIdentifer: {
		channelName: string;
		uniqueID: string;
	};
	userData?: { userDataField: UserDataField[] };
	description: {
		language: string;
		value: string;
	};
};

export type BasicAddress = {
	addressLine: string[];
	city: string;
	country: string;
	email1: string;
	email2: string;
	fax1: string;
	firstName: string;
	lastName: string;
	middleName: string;
	nickName: string;
	personTitle: string;
	phone1: string;
	phone2: string;
	state: string;
	zipCode: string;
};

type UsableShippingChargePolicy = {
	name: string;
	type: string;
	uniqueID: string;
};

type Adjustment = { currency: string; value: string };

type OrderItemAddress = BasicAddress & {
	addressId: string;
	fax2: string;
	phone1Publish: string;
	phone2Publish: string;
	postalCode: string;
	stateOrProvinceName: string;
	xitem_firstName: string;
	xitem_lastName: string;
};

export type OrderItemAdjustment = {
	code: string;
	description: string;
};

export type OrderItem = OrderItemAddress & {
	adjustment?: Array<OrderItemAdjustment>;
	availableDate: string;
	carrier: string;
	contractId: string;
	correlationGroup: string;
	createDate: string;
	currency: string;
	expectedShipDate: string;
	freeGift: string;
	fulfillmentCenterId: string;
	fulfillmentCenterName: string;
	fulfillmentCenterOwnerId: string;
	isExpedited: string;
	lastUpdateDate: string;
	offerID: string;
	orderItemFulfillmentStatus: string;
	orderItemId: string;
	orderItemInventoryStatus: string;
	orderItemPrice: string;
	orderItemStatus: string;
	partNumber: string;
	productId: string;
	quantity: string;
	salesTax: string;
	salesTaxCurrency: string;
	shipModeCode: string;
	shipModeDescription: string;
	shipModeId: string;
	shipModeLanguage: string;
	shippingCharge: string;
	shippingChargeCurrency: string;
	shippingTax: string;
	shippingTaxCurrency: string;
	totalAdjustment: Adjustment;
	unitPrice: string;
	unitQuantity: string;
	unitUom: string;
	UOM: string;
	usableShippingChargePolicy: UsableShippingChargePolicy[];
	xitem_isPersonalAddressesAllowedForShipping: string;
	xitem_memberId: string;
	shipInstruction?: string;
};
export type PaymentAddress = BasicAddress & {
	billing_address_id: string;
	fax2: string;
	phone1Publish: string;
	phone2Publish: string;
	postalCode: string;
	stateOrProvinceName: string;
};

export type Protocol = {
	name: string;
	value: string;
};

export type PaymentInstruction = PaymentAddress & {
	payMethodId: string;
	piAmount: string;
	piCurrency: string;
	piDescription: string;
	piId: string;
	piLanguage: string;
	piStatus: string;
	protocolData: Protocol[];
	xpaym_policyId: string;
	xpaym_punchoutPayment: string;
	xpaym_tokenization: string;
	paymentTermConditionId?: string;
};

export type CreditCardAccount = {
	account: string;
	expire_month: string;
	expire_year: string;
	cc_cvc: string;
	cc_brand: string;
};

export enum BANEMEX_EVO_TABS_FORM {
	CARDS = 0,
	NEW_CARD = 1,
}

export type PaymentInfo = {
	piId?: string;
	payMethodId?: string;
	piCurrency?: string;
	piDescription?: string;
	billing_address_id: string;
	numberOfInstallments?: string;
	piAmount: string;
	policyId?: string;
	card_holder?: string;
	card_number?: string;
	check_notice?: boolean;
	ready_checkout?: boolean;
	error_processing?: boolean;
	account?: string;
	issuer?: string;
	paymentMethodId?: string;
	expire_month?: string;
	expire_year?: string;
	cc_cvc?: string;
	cc_brand?: string;
	paymentTermConditionId?: string;
	valueFromPaymentTC?: string;
	dp_card_number?: string;
	txt_num?: string;
	prom_val?: string;
	card_num?: string;
	sessionid?: string;
	x_allowMultiplePI?: string;
	points_amount?: string;
	points_amount_error?: string;
	banamexEvoTabsForm?: BANEMEX_EVO_TABS_FORM;
	banamexEvoHtml?: string;
	banamexSessionIdCreateToken?: string;
	banamexEvoSelectedCardToken?: string;
	banamexEvoCardSecurityCode?: string;
	banamexEvoValidCard?: boolean;
	banamexCardNotSupported?: boolean;
	banamexNumberOfPayments?: string | number;
	banamexPlanId?: string;
};

export type PaymentActionsState = {
	paymentsCatalog?: PaymentsCatalog;
	loadingCheckoutSubmit?: boolean;
	loadingChangePayment?: boolean;
};

export type PaymentToEdit = PaymentInfo &
	PaymentActionsState & {
		nickName?: string;
		action?: PIAction;
		dirty?: boolean;
	};

/**
 * PI action indicator,
 * 'none': not touched, no call to server needed.
 * 'delete': need to call delete by piId
 * 'add': need to call add pi. Note: we can call add to add a batch(different request payload)
 * 'update': call update PI, the update PI call will update current pi and returning a new piId.
 */
export type PIAction = 'none' | 'delete' | 'add' | 'update';

export enum ORDER_STATUS_DESCRIPTION {
	A = 'Autorización de pago requiere revisión',
	B = 'En espera',
	C = 'Pago aprobado',
	D = 'Pedido completado',
	E = 'Editado actualmente por la tienda',
	F = 'Pedido transferido a centro de despacho',
	G = 'Pedido en proceso',
	I = 'Sometido',
	K = 'Devolución asociada',
	L = 'Inventario insuficiente',
	M = 'Pedido recibido y listo para ser procesado',
	N = 'Aprobación denegada',
	P = 'Pedido pendiente',
	R = 'Inventario preparado',
	S = 'Pedido enviado',
	T = 'Bloqueado actualmente por la tienda',
	W = 'Pendiente de aprobación',
	V = 'Enviado parcialmente',
	X = 'Pedido cancelado',
}

export enum ORDER_STATUS_COLORS {
	A = '#C48218', // Autorización de pago requiere revisión
	B = '#C48218', // En espera
	C = '#4AA35A', // Pago aprobado
	D = '#4AA35A', // Pedido completado
	E = '#2C2C2C', // Editado actualmente por la tienda
	F = '#2C2C2C', // Pedido transferido a centro de despacho
	G = '#C48218', // Pedido en proceso
	I = '#C48218', // Sometido
	K = '#BF0000', // Devolución asociada
	L = '#BF0000', // Inventario insuficiente
	M = '#C48218', // Pedido recibido y listo para ser procesado
	N = '#BF0000', // Aprobación denegada
	P = '#C48218', // Pedido pendiente
	R = '#C48218', // Inventario preparado
	S = '#0093E2', // Pedido enviado
	T = '#BF0000', // Bloqueado actualmente por la tienda
	W = '#C48218', // Pendiente de aprobación
	V = '#0093E2', // Enviado parcialmente
	X = '#BF0000', // Pedido cancelado
}
